import { QuestionModel } from "../../models/QuestionModel";
import { ThunkActionType, ActionTypes } from "../actions/types";
import { StoreState } from "../../models/states/StoreState";
import { hasSetting, getSettingBoolean } from "../../models/utilities/settingUtilities";
import { BlockModel } from "../../models/BlockModel";
import { EvaluationResult } from "../../expressions/EvaluationResult";
import { LogicType } from "../../models/enums/LogicType";
import { ParserState } from "../../grammar/utils/ParserState";
import { parse, cacheEvaluation, findCachedEvaluation } from "../../expressions/parser";
import { Skippable } from "../../models/Skippable";
import { ChoiceModel } from "../../models/ChoiceModel";
import { VariableModel } from "../../models/VariableModel";

interface EvaluationOptions {
    allowUnsafe?: boolean;
}

const keyForExpression = (expression: string, type = LogicType.Advanced) =>
    type === LogicType.Simple ? `simple:${expression}` : expression;

export function evaluate(
    { expression, type = LogicType.Advanced },
    { allowUnsafe }: EvaluationOptions = {},
): ThunkActionType<EvaluationResult> {
    const interviewSelector = (state: StoreState) => state.interview;

    return (dispatch, getState) => {
        // Try to find the result in cache.
        const key = keyForExpression(expression, type);
        const cachedResult = findCachedEvaluation(key);
        if (cachedResult) return new EvaluationResult(cachedResult);
        // Evaluate the expression.
        const variables = [];
        let invalidateCache = false;
        const state: ParserState = {
            interview: interviewSelector(getState()),
            didParseVariable: (variable: string) => {
                variables.push(variable.toUpperCase());
            },
            invalidateCache: () => {
                invalidateCache = true;
            },
            dispatch: (action: ActionTypes) => {
                if (!allowUnsafe) return;
                dispatch(action);
                state.interview = interviewSelector(getState());
            },
        };
        const result = parse(expression, state, type);
        // The evaluated result is not cacheable.
        if (!invalidateCache) {
            // Save in cache.
            cacheEvaluation(key, result, variables);
        }
        return new EvaluationResult(result);
    };
}

function evaluateIsVisible(skippable: Skippable): ThunkActionType<boolean> {
    return (dispatch) => {
        const shouldSkip =
            skippable.skipLogic && dispatch(evaluate(skippable.skipLogic)).asBoolean();
        const shouldDisplay =
            !skippable.displayLogic || dispatch(evaluate(skippable.displayLogic)).asBoolean();
        return !shouldSkip && shouldDisplay;
    };
}

export function evaluateIfChoiceVisible(choice: ChoiceModel): ThunkActionType<boolean> {
    return (dispatch) => dispatch(evaluateIsVisible(choice));
}

export function evaluateIsVariableVisible(variable: VariableModel): ThunkActionType<boolean> {
    return (dispatch, getState) => {
        const serverPage = getState().interview.navigation?.currentPageOnServer;

        if (serverPage) {
            return serverPage.questions
                .flatMap((q) => q.variables)
                .some((v) => v.name === variable.name);
        }

        return dispatch(evaluateIsVisible(variable));
    };
}

export function evaluateIsQuestionVisible(question: QuestionModel): ThunkActionType<boolean> {
    const previewQuestionSelector = (state: StoreState) =>
        state.interview.navigation.currentPage?.previewQuestion;

    return (dispatch, getState) => {
        if (previewQuestionSelector(getState()) === question.name) return true;
        if (hasSetting(question, "Active") && !getSettingBoolean(question, "Active")) return false;
        if (
            getState().interview.navigation.currentPageOnServer?.questions?.some(
                (q) => q.name === question.name,
            )
        )
            return true;
        return dispatch(evaluateIsVisible(question));
    };
}

export function evaluateIsBlockVisible(block: BlockModel): ThunkActionType<boolean> {
    return (dispatch) => block.questions.length && dispatch(evaluateIsVisible(block));
}
